import { useEffect, useState } from 'react';
import { TextInput } from '@carbon/react';
import { parseRangeBound } from './value-histogram';

interface BoundInputProps {
  id: string;
  label: string;
  /** The staged bound, or undefined when that end is open. */
  staged: unknown;
  /** Shown while the end is open (or blank): the value it falls back to. */
  fullValue: number;
  invalid: boolean;
  invalidText?: string;
  disabled?: boolean;
  onCommit: (value: number | null) => void;
}

/**
 * One end of a numeric range. Keeps its own text while typing ("-", "12.") and
 * commits only complete numbers; follows outside changes, and a blank or
 * half-typed end snaps back to the applied value on blur.
 * @param {BoundInputProps} props The bound and how to commit it.
 * @return {JSX.Element} The input.
 */
export const BoundInput = ({
  id,
  label,
  staged,
  fullValue,
  invalid,
  invalidText = 'Min is more than Max; the range between them is used',
  disabled = false,
  onCommit,
}: BoundInputProps) => {
  const committed = parseRangeBound(staged) ?? fullValue;
  const [text, setText] = useState(String(committed));

  useEffect(() => {
    setText((current) => {
      if (parseRangeBound(current) === committed) return current;
      return String(committed);
    });
  }, [committed]);

  return (
    <TextInput
      id={id}
      labelText={label}
      size="sm"
      inputMode="decimal"
      value={text}
      disabled={disabled}
      invalid={invalid}
      invalidText={invalidText}
      onChange={(event) => {
        setText(event.target.value);
        const number = parseRangeBound(event.target.value.trim());
        if (number !== null) onCommit(number);
      }}
      onBlur={() => {
        if (parseRangeBound(text.trim()) === null) setText(String(committed));
      }}
    />
  );
};
